(() => {
  if (customElements.get('emz-article-navigation')) return;

  class EmzArticleNavigation extends HTMLElement {
    connectedCallback() {
      this.mountTicket = (this.mountTicket || 0) + 1;
      const ticket = this.mountTicket;
      const ready = () => {
        if (this.isConnected && ticket === this.mountTicket) this.mount();
      };
      if (document.readyState === 'loading') {
        this.readyListener = ready;
        document.addEventListener('DOMContentLoaded', ready, { once: true });
      } else {
        queueMicrotask(ready);
      }
    }

    mount() {
      if (this.links) return;
      const body = this.nextElementSibling;
      const list = this.querySelector('.emz-article-navigation__links');
      if (!body?.matches('.article__body') || !list) return;
      const headings = Array.from(body.querySelectorAll('h2, h3'))
        .filter(heading => heading.textContent.trim() && this.headingAvailable(heading, body));
      if (headings.length < 4) return;

      this.links = new Map();
      this.ownedAttributes = new Map();
      const prefix = `emz-${this.dataset.sectionId || 'article'}-section`;
      const fragment = document.createDocumentFragment();
      headings.forEach((heading, index) => {
        const owned = {};
        if (!heading.id) {
          const base = `${prefix}-${index + 1}`;
          let id = base;
          let suffix = 2;
          while (document.getElementById(id)) id = `${base}-${suffix++}`;
          heading.id = id;
          owned.id = id;
        }
        this.ownedAttributes.set(heading, owned);
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${encodeURIComponent(heading.id)}`;
        link.textContent = heading.textContent.trim().replace(/\s+/g, ' ');
        item.append(link);
        fragment.append(item);
        this.links.set(link, heading);
      });
      list.replaceChildren(fragment);
      this.onLinkClick = event => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const link = event.target.closest('a');
        const heading = this.links.get(link);
        if (!heading || event.detail !== 0) return;
        if (!heading.hasAttribute('tabindex')) {
          heading.setAttribute('tabindex', '-1');
          this.ownedAttributes.get(heading).tabindex = true;
        }
        heading.focus({ preventScroll: true });
      };
      this.addEventListener('click', this.onLinkClick);
      this.hidden = false;

      let fragmentId;
      try { fragmentId = decodeURIComponent(window.location.hash.slice(1)); } catch (_) { return; }
      const target = headings.find(heading => heading.id === fragmentId && this.ownedAttributes.get(heading).id);
      if (target) this.scheduleFragmentAlignment(target, fragmentId);
    }

    scheduleFragmentAlignment(target, fragmentId) {
      this.cancelFragmentAlignment();
      this.fragmentIntentListener = event => {
        if (event.isTrusted) this.cancelFragmentAlignment();
      };
      ['wheel', 'touchstart', 'pointerdown', 'keydown'].forEach(type => {
        window.addEventListener(type, this.fragmentIntentListener, { capture: true, passive: true });
      });
      this.fragmentLoadListener = () => {
        window.removeEventListener('load', this.fragmentLoadListener);
        this.fragmentLoadListener = null;
        this.fragmentFrame = requestAnimationFrame(() => {
          this.fragmentFrame = undefined;
          this.cancelFragmentAlignment();
          let currentId;
          try { currentId = decodeURIComponent(window.location.hash.slice(1)); } catch (_) { return; }
          if (this.isConnected && target.isConnected && target.id === fragmentId && currentId === fragmentId) {
            target.scrollIntoView({ block: 'start', behavior: 'instant' });
          }
        });
      };
      if (document.readyState === 'complete') {
        this.fragmentLoadListener();
      } else {
        window.addEventListener('load', this.fragmentLoadListener, { once: true });
      }
    }

    cancelFragmentAlignment() {
      window.removeEventListener('load', this.fragmentLoadListener);
      ['wheel', 'touchstart', 'pointerdown', 'keydown'].forEach(type => {
        window.removeEventListener(type, this.fragmentIntentListener, true);
      });
      if (this.fragmentFrame !== undefined) cancelAnimationFrame(this.fragmentFrame);
      this.fragmentFrame = undefined;
      this.fragmentLoadListener = null;
      this.fragmentIntentListener = null;
    }

    headingAvailable(heading, body) {
      for (let node = heading; node && node !== body.parentElement; node = node.parentElement) {
        if (node.hidden || node.getAttribute('aria-hidden')?.trim().toLowerCase() === 'true') return false;
        if (node.tagName === 'DETAILS' && !node.open) {
          const summary = Array.from(node.children).find(child => child.tagName === 'SUMMARY');
          if (!summary?.contains(heading)) return false;
        }
      }
      return true;
    }

    disconnectedCallback() {
      this.mountTicket = (this.mountTicket || 0) + 1;
      document.removeEventListener('DOMContentLoaded', this.readyListener);
      this.removeEventListener('click', this.onLinkClick);
      this.cancelFragmentAlignment();
      this.ownedAttributes?.forEach((owned, heading) => {
        if (owned.id && heading.id === owned.id) heading.removeAttribute('id');
        if (owned.tabindex && heading.getAttribute('tabindex') === '-1') heading.removeAttribute('tabindex');
      });
      this.querySelector('.emz-article-navigation__links')?.replaceChildren();
      this.links = null;
      this.ownedAttributes = null;
      this.hidden = true;
    }
  }

  customElements.define('emz-article-navigation', EmzArticleNavigation);
})();
